/**
 * CampaignComposer component - form for creating marketing campaigns
 */

import { useState } from 'react';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { EmailPreview } from './EmailPreview';
import { Send, Save } from 'lucide-react';

interface CampaignComposerProps {
  defaultServiceTypeName?: string;
  selectedLeadsCount: number;
  onSave: (data: {
    name: string;
    subject: string;
    templateContent: string;
  }) => Promise<void>;
  onSend?: () => void;
  isLoading?: boolean;
}

const DEFAULT_TEMPLATE = `
<!DOCTYPE html>
<html lang="es">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
</head>
<body style="margin: 0; padding: 0; font-family: Arial, sans-serif; background-color: #f4f4f5;">
  <table role="presentation" style="width: 100%; max-width: 600px; margin: 0 auto; background-color: #ffffff;">
    <tr>
      <td style="padding: 40px;">
        <h2 style="color: #1f2937;">Hola {{customer_name}},</h2>

        <p style="color: #4b5563; line-height: 1.6;">
          Le escribimos para recordarle que su vehículo {{vehicle_make}} {{vehicle_model}} ({{vehicle_registration}}) puede necesitar atención.
        </p>

        <p style="color: #4b5563; line-height: 1.6;">
          Reserve su cita online de forma rápida y sencilla:
        </p>

        <p style="text-align: center; margin: 30px 0;">
          <a href="{{booking_link}}" style="background-color: #2563eb; color: #ffffff; padding: 12px 24px; text-decoration: none; border-radius: 6px; font-weight: bold;">
            Reservar Cita
          </a>
        </p>

        <p style="color: #6b7280; font-size: 14px;">
          Gracias por confiar en nosotros.
        </p>

        <p style="color: #6b7280; font-size: 14px;">
          <strong>Leal Chapa y Pintura</strong><br>
          Tel: +34 912 345 678
        </p>
      </td>
    </tr>
  </table>
</body>
</html>
`.trim();

export function CampaignComposer({
  defaultServiceTypeName,
  selectedLeadsCount,
  onSave,
  onSend,
  isLoading,
}: CampaignComposerProps) {
  const [name, setName] = useState('');
  const [subject, setSubject] = useState(
    defaultServiceTypeName
      ? `Es hora de su ${defaultServiceTypeName} - Leal Chapa y Pintura`
      : 'Es hora de cuidar su vehículo - Leal Chapa y Pintura'
  );
  const [templateContent, setTemplateContent] = useState(DEFAULT_TEMPLATE);

  const handleSave = async () => {
    await onSave({ name, subject, templateContent });
  };

  const canSave = name.trim() && subject.trim() && templateContent.trim() && selectedLeadsCount > 0;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Configuración de campaña</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="campaign-name">Nombre de la campaña</Label>
            <Input
              id="campaign-name"
              placeholder="Ej: Recordatorio cambio de aceite - Enero 2025"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={isLoading}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="email-subject">Asunto del email</Label>
            <Input
              id="email-subject"
              placeholder="Asunto del email"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              disabled={isLoading}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="email-template">Contenido del email (HTML)</Label>
            <textarea
              id="email-template"
              className="w-full h-64 p-3 text-sm font-mono border rounded-md resize-none focus:outline-none focus:ring-2 focus:ring-ring"
              value={templateContent}
              onChange={(e) => setTemplateContent(e.target.value)}
              disabled={isLoading}
            />
          </div>

          <div className="p-3 bg-muted rounded-md">
            <p className="text-sm">
              <strong>Destinatarios seleccionados:</strong> {selectedLeadsCount}
            </p>
          </div>

          <div className="flex gap-2">
            <Button
              onClick={handleSave}
              disabled={!canSave || isLoading}
              className="flex-1"
            >
              <Save className="h-4 w-4 mr-2" />
              Guardar borrador
            </Button>
            {onSend && (
              <Button
                variant="default"
                onClick={onSend}
                disabled={!canSave || isLoading}
                className="flex-1"
              >
                <Send className="h-4 w-4 mr-2" />
                Guardar y enviar
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      <EmailPreview subject={subject} htmlContent={templateContent} />
    </div>
  );
}
